import { computed, getCurrentScope, onScopeDispose } from "vue";
import { useI18n } from "vue-i18n";

import startCase from "lodash/startCase";

import { AnalyticsEvent } from "@/enums/analytics";
import { ModifierKeys, ShortcutContext } from "@/enums/keyboardShortcuts";
import { OPEN_TEMPLATES } from "@/helpers/constants/events";
import { formatUserFullName } from "@/helpers/formatting";
import { useAuth } from "@/hooks/auth";
import { useEmitter } from "@/hooks/common";
import { useAnalyticsStore } from "@/stores/analytics";
import { useKeyboardShortcutsStore } from "@/stores/keyboardShortcuts";

import { isMacOs, metaKey } from "./normalizeShortcut";
import { destroyRegistry, registerHandler } from "./shortcutRegistry";

import type { TextEditorShortcut } from "@/enums/keyboardShortcuts";
import type { UserKeyboardShortcut } from "@/models/options";
import type { Quill } from "@vueup/vue-quill";

const SHORTCUT_GROUPS = [
  { i18nKey: "SHORTCUTS.GLOBAL", context: ShortcutContext.Global },
  { i18nKey: "SHORTCUTS.DEALS_LIST", context: ShortcutContext.DealsList },
  { i18nKey: "SHORTCUTS.ACTIVE_DEAL", context: ShortcutContext.ActiveDeal },
  { i18nKey: "SHORTCUTS.INBOX", context: ShortcutContext.Inbox },
  {
    i18nKey: "SHORTCUTS.CALL_DISPOSITION",
    context: ShortcutContext.CallDisposition,
  },
  { i18nKey: "SHORTCUTS.EMAIL_SMS", context: ShortcutContext.EmailSms },
] as const;

export const useKeyboardShortcuts = () => {
  const auth = useAuth();
  const { t } = useI18n();
  const emitter = useEmitter();
  const { trackEvent } = useAnalyticsStore();
  const store = useKeyboardShortcutsStore();

  const groupedShortcuts = computed(() =>
    SHORTCUT_GROUPS.map(({ i18nKey, context }) => ({
      contextKey: context,
      context: t(i18nKey),
      shortcuts: store.configuredShortcuts.filter(
        (sc) => sc.context === context
      ),
    })).filter((group) => group.shortcuts.length > 0)
  );

  const getShortcutForAction = (actionName: string) => {
    return store.shortcutsByAction.get(actionName);
  };

  const assignShortcutToAction = ({
    actionName,
    action,
    canPerformAction = () => true,
  }: {
    actionName: string;
    action: () => void;
    canPerformAction?: () => boolean;
  }) => {
    const wrappedCallback = () => {
      action();
      if (auth.currentUser) {
        const shortcut = getShortcutForAction(actionName);
        const userName =
          auth.currentUser.full_name ?? formatUserFullName(auth.currentUser);
        trackEvent(AnalyticsEvent.KeyboardShortcutTriggered, {
          actionName,
          shortcutKeys: shortcut?.keys,
          modifiers: shortcut?.modifiers,
          context: shortcut?.context,
          user: userName,
          role: auth.currentUser.roles?.join(", ") ?? "unknown",
        });
      }
    };

    const cleanup = registerHandler({
      actionName,
      callback: wrappedCallback,
      canPerform: canPerformAction,
    });

    // Auto-cleanup when the calling component's effect scope is disposed
    if (getCurrentScope()) {
      onScopeDispose(cleanup);
    }
  };

  // ---------------------------------------------------------------------------
  // Quill editor shortcuts (uses Quill's native keyboard module – unchanged)
  // ---------------------------------------------------------------------------

  type BindingDescriptor = {
    key: string;
    [ModifierKeys.Alt]?: boolean;
    [ModifierKeys.Ctrl]?: boolean;
    [ModifierKeys.Meta]?: boolean;
    [ModifierKeys.Shift]?: boolean;
  };

  const MODIFIER_KEYS = [
    ModifierKeys.Alt,
    ModifierKeys.Ctrl,
    ModifierKeys.Meta,
    ModifierKeys.Shift,
  ] as const;

  const buildBindingDescriptor = (
    shortcut: UserKeyboardShortcut
  ): BindingDescriptor => {
    return (shortcut.modifiers ?? []).reduce<BindingDescriptor>(
      (desc, modifier) => {
        if ((MODIFIER_KEYS as readonly ModifierKeys[]).includes(modifier)) {
          desc[modifier] = true;
        }
        return desc;
      },
      { key: shortcut.keys[0].toUpperCase() }
    );
  };

  /* eslint-disable @typescript-eslint/no-explicit-any */
  const handleQuillShortcut = ({
    quill,
    actionName,
    range,
    context,
  }: {
    quill: Quill;
    actionName: TextEditorShortcut;
    range: any;
    context: any;
  }): boolean => {
    if (!range) {
      return false;
    }

    switch (actionName) {
      case "bold":
      case "italic":
      case "underline":
      case "strike": {
        const isOn = !!context.format[actionName];
        quill.format(actionName, !isOn);
        return false;
      }

      case "orderedList": {
        const isOn = context.format.list === "ordered";
        quill.format("list", isOn ? false : "ordered");
        return false;
      }

      case "unorderedList": {
        const isOn = context.format.list === "bullet";
        quill.format("list", isOn ? false : "bullet");
        return false;
      }

      case "link": {
        const toolbar = quill.getModule("toolbar");
        if (toolbar?.handlers?.link) {
          const isLink = !!context.format.link;
          toolbar.handlers.link.call(toolbar, !isLink);
        } else {
          // fallback
          const prevValue = context.format.link || "";
          const value = window.prompt("Enter link", prevValue || "https://");
          if (value) {
            quill.format("link", value);
          } else if (prevValue) {
            quill.format("link", false);
          }
        }
        return false;
      }

      case "chooseTemplate": {
        emitter.emit(OPEN_TEMPLATES);
        return false;
      }

      default: {
        return false;
      }
    }
  };

  const registerQuillShortcuts = ({
    quill,
    shortcuts,
  }: {
    quill: Quill;
    shortcuts: UserKeyboardShortcut[];
  }) => {
    const kb = quill.getModule("keyboard");

    shortcuts.forEach((shortcut) => {
      const desc = buildBindingDescriptor(shortcut);
      kb.addBinding(desc, (range: any, context: any) => {
        return handleQuillShortcut({
          quill,
          actionName: shortcut.action_name as TextEditorShortcut,
          range,
          context,
        });
      });
    });
  };
  /* eslint-enable @typescript-eslint/no-explicit-any */

  return {
    metaKey,
    getShortcutForAction,
    assignShortcutToAction,
    isMacOs,
    userShortcuts: store.userShortcuts,
    configuredShortcuts: computed(() => store.configuredShortcuts),
    groupedShortcuts,
    formatActionName: startCase,
    registerQuillShortcuts,
  };
};

// Clean up singleton registry state on HMR to prevent duplicate listeners.
if (import.meta.hot) {
  import.meta.hot.dispose(() => {
    destroyRegistry();
  });
}
